import type { BoqRow, MatchCandidate } from '../../types'
import {
  codeFragments,
  extractSearchTokens,
  extractSpecificationNumbers,
  normalizeUnit,
} from '../normalization'
import { scoreRowSimilarity } from './similarity'

export interface CandidateIndex {
  rows: readonly BoqRow[]
  byUnit: ReadonlyMap<string, readonly BoqRow[]>
  byToken: ReadonlyMap<string, readonly BoqRow[]>
  bySpecificationNumber: ReadonlyMap<string, readonly BoqRow[]>
  byCodeFragment: ReadonlyMap<string, readonly BoqRow[]>
}

export interface FuzzyCandidateSearch {
  candidates: readonly MatchCandidate[]
  evaluatedCandidateCount: number
  totalIndexedCandidates: number
}

function isCandidateIndex(value: readonly BoqRow[] | CandidateIndex): value is CandidateIndex {
  return !Array.isArray(value)
}

function addToIndex(index: Map<string, BoqRow[]>, key: string, row: BoqRow): void {
  if (!key) return
  const rows = index.get(key)
  if (rows === undefined) index.set(key, [row])
  else rows.push(row)
}

function sortRows(rows: readonly BoqRow[]): BoqRow[] {
  return [...rows].sort(
    (left, right) =>
      left.sourceRowNumber - right.sourceRowNumber || left.id.localeCompare(right.id),
  )
}

export function buildCandidateIndex(targetRows: readonly BoqRow[]): CandidateIndex {
  const rows = sortRows(targetRows)
  const byUnit = new Map<string, BoqRow[]>()
  const byToken = new Map<string, BoqRow[]>()
  const bySpecificationNumber = new Map<string, BoqRow[]>()
  const byCodeFragment = new Map<string, BoqRow[]>()

  for (const row of rows) {
    addToIndex(byUnit, normalizeUnit(row.unit), row)
    for (const token of extractSearchTokens(row.itemName, row.itemDescription)) {
      addToIndex(byToken, token, row)
    }
    for (const number of extractSpecificationNumbers(`${row.itemName} ${row.itemDescription}`)) {
      addToIndex(bySpecificationNumber, number, row)
    }
    for (const fragment of codeFragments(row.itemCode)) addToIndex(byCodeFragment, fragment, row)
  }
  return { rows, byUnit, byToken, bySpecificationNumber, byCodeFragment }
}

function resolvedMaximum(value: number | undefined): number {
  return value !== undefined && Number.isInteger(value) && value > 0 ? value : 50
}

export function searchFuzzyCandidates(
  baselineRow: BoqRow,
  targetRowsOrIndex: readonly BoqRow[] | CandidateIndex,
  options: { maxCandidates?: number } = {},
): FuzzyCandidateSearch {
  const index: CandidateIndex = isCandidateIndex(targetRowsOrIndex)
    ? targetRowsOrIndex
    : buildCandidateIndex(targetRowsOrIndex)
  const prefilterScores = new Map<string, { row: BoqRow; score: number }>()
  const addRows = (rows: readonly BoqRow[] | undefined, score: number): void => {
    if (rows === undefined) return
    for (const row of rows) {
      const existing = prefilterScores.get(row.id)
      prefilterScores.set(row.id, { row, score: (existing?.score ?? 0) + score })
    }
  }

  addRows(index.byUnit.get(normalizeUnit(baselineRow.unit)), 30)
  for (const token of extractSearchTokens(baselineRow.itemName, baselineRow.itemDescription)) {
    addRows(index.byToken.get(token), token.startsWith('#') ? 6 : 2)
  }
  for (const number of extractSpecificationNumbers(
    `${baselineRow.itemName} ${baselineRow.itemDescription}`,
  )) {
    addRows(index.bySpecificationNumber.get(number), 8)
  }
  for (const fragment of codeFragments(baselineRow.itemCode)) {
    addRows(index.byCodeFragment.get(fragment), 3)
  }

  const rankedPool =
    prefilterScores.size > 0
      ? [...prefilterScores.values()]
      : index.rows.map((row) => ({ row, score: 0 }))
  rankedPool.sort(
    (left, right) =>
      right.score - left.score ||
      left.row.sourceRowNumber - right.row.sourceRowNumber ||
      left.row.id.localeCompare(right.row.id),
  )

  const evaluated = rankedPool.slice(0, resolvedMaximum(options.maxCandidates))
  const candidates = evaluated.map(({ row }) => {
    const fieldScores = scoreRowSimilarity(baselineRow, row)
    return {
      targetRow: row,
      score: fieldScores.weightedScore,
      fieldScores,
      reason: '按项目名称、项目特征、单位和编码局部信息计算',
    }
  })
  candidates.sort(
    (left, right) =>
      right.score - left.score ||
      left.targetRow.sourceRowNumber - right.targetRow.sourceRowNumber ||
      left.targetRow.id.localeCompare(right.targetRow.id),
  )
  return {
    candidates,
    evaluatedCandidateCount: evaluated.length,
    totalIndexedCandidates: rankedPool.length,
  }
}

export function findFuzzyCandidates(
  baselineRow: BoqRow,
  targetRowsOrIndex: readonly BoqRow[] | CandidateIndex,
  options: { maxCandidates?: number } = {},
): readonly MatchCandidate[] {
  return searchFuzzyCandidates(baselineRow, targetRowsOrIndex, options).candidates
}
